import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Phone,
  CheckCircle,
  ArrowRight,
  Users,
  Briefcase,
  HeartHandshake,
  Sparkles,
  Compass,
  Home as HomeIcon,
} from 'lucide-react';
import StripePaymentButton from '../components/StripePaymentButton';
import './WomensRecovery.css';

const WomensRecovery = () => {
  const whoWeServe = [
    { icon: HomeIcon, text: 'Transitioning from treatment programs' },
    { icon: Sparkles, text: 'Seeking recovery-focused housing' },
    { icon: HeartHandshake, text: 'Working toward family reunification' },
    { icon: Briefcase, text: 'Building employment and life skills' },
    { icon: Users, text: 'Looking for community support and accountability' },
    { icon: Compass, text: 'Seeking stability and long-term recovery' },
  ];

  const services = [
    'Recovery Accountability & Wellness Support',
    'Recovery Resource Navigation',
    'Employment Readiness Support',
    'Life Skills Development',
    'Recovery Meeting Engagement',
    'Family Reconnection Support',
    'Case Management & Goal Planning',
    'Community Connections and Referrals',
  ];

  const whyChoose = [
    'DDAP Licensed Recovery Housing',
    'Recovery-Focused Environment',
    'Women-Centered Support',
    'Employment & Life Skills Support',
    'Community Resource Navigation',
    'Family Reconnection Resources',
  ];

  const communityImpact = [
    'Maintain recovery',
    'Secure employment',
    'Build independent living skills',
    'Strengthen family relationships',
    'Access community resources',
    'Create stable and productive lives',
  ];

  const expectations = [
    'Maintain complete sobriety and abstinence from all substances',
    'Participate in recovery meetings and support activities',
    'Maintain daily routines and work toward personal goals',
    'Follow all house guidelines and community standards',
    'Contribute to a supportive peer environment',
    'Work toward long-term recovery and stability',
  ];

  return (
    <div className="recovery-page" data-testid="womens-recovery-page">
      <section className="page-hero">
        <div className="hero-container">
          <h1 className="page-title">Women&apos;s Recovery Program</h1>
          <p className="page-subtitle">Recovery. Stability. Opportunity.</p>
        </div>
      </section>

      <section className="page-content">
        <div className="content-container">
          <div className="content-grid">
            <div className="main-content">
              {/* About the Program */}
              <div className="content-section">
                <h2 className="content-heading">About the Program</h2>
                <p className="content-text">
                  ProSolve Global Solutions Women&apos;s Recovery House provides safe, structured recovery housing and recovery support services for women seeking stability, accountability, and a pathway forward.
                </p>
                <p className="content-text">
                  We specialize in supporting women in recovery and women working toward independent living. Our program combines safe, structured housing with recovery support services designed to help women rebuild their lives.
                </p>
              </div>

              {/* Photo Gallery */}
              <div className="photo-gallery-section">
                <h2 className="gallery-heading">Our Facility</h2>
                <div className="gallery-grid">
                  <div className="gallery-item">
                    <img src="/gallery/womens-01.jpg" alt="Women's Recovery House" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-02.jpg" alt="Women's Recovery House Interior" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-03.jpg" alt="Women's Recovery House Space" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-04.jpg" alt="Women's Recovery House Living Area" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-05.jpg" alt="Women's Recovery House Facility" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-06.jpg" alt="Women's Recovery House Room" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-07.jpg" alt="Women's Recovery House Common Space" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-08.jpg" alt="Women's Recovery House Exterior" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                  <div className="gallery-item">
                    <img src="/gallery/womens-09.jpg" alt="Women's Recovery House Environment" className="gallery-image" loading="lazy" width="400" height="300" />
                  </div>
                </div>
              </div>

              {/* Who We Serve */}
              <div className="content-section">
                <h2 className="content-heading">Who We Serve</h2>
                <p className="content-text">
                  We support women who are:
                </p>
                <div className="icon-grid">
                  {whoWeServe.map(({ icon: Icon, text }, index) => (
                    <div key={index} className="icon-grid-card" data-testid={`who-we-serve-item-${index}`}>
                      <div className="icon-grid-icon">
                        <Icon size={22} />
                      </div>
                      <p className="icon-grid-text">{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div className="content-section">
                <h2 className="content-heading">Our Services</h2>
                <p className="content-text">
                  Our program combines safe, structured housing with recovery support services designed to help women rebuild their lives.
                </p>
                <ul className="feature-list">
                  {services.map((service, index) => (
                    <li key={index} className="feature-item" data-testid={`service-item-${index}`}>
                      <CheckCircle className="feature-icon" size={24} />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Why Women Choose ProSolve */}
              <div className="content-section">
                <h2 className="content-heading">Why Women Choose ProSolve</h2>
                <div className="expectations-grid">
                  {whyChoose.map((reason, index) => (
                    <div key={index} className="expectation-card" data-testid={`why-choose-item-${index}`}>
                      <div className="expectation-number">
                        <CheckCircle size={20} />
                      </div>
                      <p className="expectation-text">{reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Community Impact */}
              <div className="content-section impact-section">
                <h2 className="content-heading">Community Impact</h2>
                <p className="content-text">
                  Our mission goes beyond housing. We help women:
                </p>
                <ul className="impact-list">
                  {communityImpact.map((item, index) => (
                    <li key={index} className="impact-item" data-testid={`impact-item-${index}`}>
                      <Sparkles className="impact-icon" size={18} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="content-text impact-closing">
                  This approach reflects our mission and helps referral sources, treatment providers, community partners, and potential funders understand the full value and impact of our Women&apos;s Recovery Program.
                </p>
              </div>

              {/* Resident Expectations */}
              <div className="content-section">
                <h2 className="content-heading">Resident Expectations</h2>
                <div className="expectations-grid">
                  {expectations.map((expectation, index) => (
                    <div key={index} className="expectation-card">
                      <div className="expectation-number">{index + 1}</div>
                      <p className="expectation-text">{expectation}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="special-note">
                <h3 className="note-title">A Supportive Recovery Community</h3>
                <p className="note-text">
                  We welcome women who are committed to maintaining their recovery and rebuilding their lives in a safe, structured environment. Our program provides the housing, accountability, and recovery support needed to thrive in long-term sobriety.
                </p>
              </div>

              <div className="cta-section">
                <h3 className="cta-heading">Ready to Begin Your Recovery Journey?</h3>
                <div className="cta-buttons">
                  <Link to="/apply" className="btn btn-primary" data-testid="apply-cta-button">
                    Apply Now
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            </div>

            <aside className="sidebar">
              <div className="info-card">
                <h3 className="info-card-title">Location</h3>
                <div className="info-item">
                  <MapPin size={20} className="info-icon" />
                  <div>
                    <p className="info-text">Catasauqua, PA</p>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <h3 className="info-card-title">Contact</h3>
                <div className="info-item">
                  <Phone size={20} className="info-icon" />
                  <a href="tel:610-443-0175" className="info-link">
                    610-443-0175
                  </a>
                </div>
              </div>

              <div className="info-card payment-card">
                <h3 className="info-card-title">Make a Payment</h3>
                <p className="payment-card-text">Submit your housing payment securely online</p>
                <div className="sidebar-stripe-button">
                  <StripePaymentButton />
                </div>
              </div>

              <div className="info-card">
                <h3 className="info-card-title">Quick Links</h3>
                <div className="quick-links">
                  <Link to="/mens-recovery" className="sidebar-link">
                    Men&apos;s Recovery House
                  </Link>
                  <Link to="/testimonials" className="sidebar-link">
                    Read Testimonials
                  </Link>
                  <Link to="/contact" className="sidebar-link">
                    Contact Us
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WomensRecovery;
